<template>
  <v-dialog v-model="visible" max-width="860px" persistent>
    <v-card class="profile-dialog-card entity-form-dialog department-expenses">
      <EntityDialogHeader
        eyebrow="Department finances"
        title="Monthly expenses"
        :subtitle="department ? `Allocate teacher pay and operating expenses for ${department.name}.` : 'Allocate department expenses.'"
        icon="mdi-receipt-text-outline"
        close-label="Close monthly expenses"
        @close="close"
      />
      <v-divider />
      <v-card-text class="profile-dialog-body entity-form-dialog__body">
        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">{{ editingId || editingGroupKey ? "mdi-pencil-outline" : "mdi-plus" }}</v-icon></span>
            <div>
              <h3>{{ editingId || editingGroupKey ? "Edit monthly expense" : "Add monthly expense" }}</h3>
              <p>Add a department cost or split one staff salary across multiple departments.</p>
            </div>
          </div>
          <v-row>
            <v-col cols="12" sm="4">
              <v-select v-model="form.category" :items="categories" label="Category" outlined hide-details @change="categoryChanged" />
            </v-col>
            <v-col v-if="form.category === 'TEACHER'" cols="12" sm="5">
              <v-autocomplete v-model="form.TeacherId" :items="teachers" item-text="name" item-value="id" label="Teacher" outlined :loading="loadingTeachers" :error-messages="submitted && !form.TeacherId ? ['Teacher is required'] : []" :hide-details="!submitted || Boolean(form.TeacherId)" />
            </v-col>
            <v-col v-else-if="form.category === 'OTHER'" cols="12" sm="5">
              <v-text-field v-model.trim="form.name" label="Expense name" placeholder="Rent, electricity, supplies…" outlined :error-messages="submitted && !form.name ? ['Expense name is required'] : []" :hide-details="!submitted || Boolean(form.name)" />
            </v-col>
            <v-col v-if="form.category !== 'STAFF'" cols="12" sm="3">
              <v-text-field v-model="form.amount" type="number" min="0.01" step="0.01" prefix="$" label="Monthly amount" outlined :error-messages="submitted && !(Number(form.amount) > 0) ? ['Enter an amount'] : []" :hide-details="!submitted || Number(form.amount) > 0" />
            </v-col>
            <template v-if="form.category === 'STAFF'">
              <v-col cols="12" sm="4">
                <v-autocomplete v-model="form.TeacherId" :items="teachers" item-text="name" item-value="id" label="Staff member" outlined :loading="loadingTeachers" :error-messages="submitted && !form.TeacherId ? ['Staff member is required'] : []" :hide-details="!submitted || Boolean(form.TeacherId)" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model.trim="form.staffRole" label="Role" placeholder="Principal, coordinator…" outlined :error-messages="submitted && !form.staffRole ? ['Role is required'] : []" :hide-details="!submitted || Boolean(form.staffRole)" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-text-field v-model="form.totalAmount" type="number" min="0.01" step="0.01" prefix="$" label="Total monthly pay" outlined :error-messages="submitted && !(Number(form.totalAmount) > 0) ? ['Enter monthly pay'] : []" :hide-details="!submitted || Number(form.totalAmount) > 0" />
              </v-col>
              <v-col cols="12" sm="8">
                <v-autocomplete v-model="form.ClassIds" :items="departments" item-text="name" item-value="id" multiple chips small-chips deletable-chips label="Responsible departments" outlined :error-messages="submitted && form.ClassIds.length < 2 ? ['Choose at least two departments'] : []" :hide-details="!submitted || form.ClassIds.length >= 2" />
              </v-col>
              <v-col cols="12" sm="4">
                <v-select v-model="form.allocationMode" :items="allocationModes" label="Split method" outlined hide-details />
              </v-col>
              <v-col v-if="form.allocationMode === 'PERCENTAGE'" cols="12">
                <div class="department-expenses__allocation-grid">
                  <div v-for="item in selectedDepartments" :key="item.id" class="department-expenses__allocation-row">
                    <span>{{ item.name }}</span>
                    <v-text-field v-model.number="form.allocations[item.id]" type="number" min="0.01" max="100" suffix="%" outlined dense hide-details />
                    <strong>{{ allocatedAmount(item.id) | currency }}</strong>
                  </div>
                  <div class="department-expenses__allocation-total" :class="{ 'error--text': !validPercentTotal }">
                    <span>Total allocation</span><strong>{{ percentTotal.toFixed(2) }}%</strong>
                  </div>
                </div>
              </v-col>
              <v-col v-else-if="form.ClassIds.length" cols="12">
                <div class="entity-form-dialog__summary">
                  <v-icon color="primary" size="18">mdi-call-split</v-icon>
                  <span>{{ form.ClassIds.length }} departments will share {{ Number(form.totalAmount || 0) | currency }} equally — approximately {{ equalShare | currency }} each.</span>
                </div>
              </v-col>
            </template>
            <v-col cols="12">
              <v-text-field v-model.trim="form.note" label="Internal note (optional)" outlined hide-details />
            </v-col>
          </v-row>
          <div class="department-expenses__form-actions">
            <v-btn v-if="editingId || editingGroupKey" text :disabled="saving" @click="resetForm">Cancel edit</v-btn>
            <v-btn color="primary" depressed :loading="saving" @click="save">
              <v-icon left small>{{ editingId || editingGroupKey ? "mdi-content-save-outline" : "mdi-plus" }}</v-icon>
              {{ editingId || editingGroupKey ? "Save changes" : "Add expense" }}
            </v-btn>
          </div>
        </section>

        <section class="entity-form-dialog__section">
          <div class="entity-form-dialog__section-heading">
            <span><v-icon size="19" color="primary">mdi-format-list-bulleted</v-icon></span>
            <div>
              <h3>Allocated expenses</h3>
              <p>These amounts are included in the department's monthly surplus or shortfall.</p>
            </div>
          </div>
          <div class="entity-form-dialog__summary department-expenses__total">
            <v-icon color="primary" size="18">mdi-calculator-variant-outline</v-icon>
            <span>Total allocated expense</span>
            <strong>{{ total | currency }} / month</strong>
          </div>
          <v-data-table class="department-expenses__table" :headers="headers" :items="expenses" :loading="loading" :items-per-page="-1" hide-default-footer mobile-breakpoint="600">
            <template v-slot:item.category="{ item }"><span>{{ categoryLabel(item) }}</span><small v-if="item.staffRole" class="d-block grey--text">{{ item.staffRole }}</small></template>
            <template v-slot:item.name="{ item }"><strong>{{ item.Teacher ? item.Teacher.name : item.name }}</strong><small v-if="item.allocationGroupKey" class="d-block grey--text">{{ Number(item.allocationPercent || 0).toFixed(2) }}% of {{ item.totalAmount | currency }}</small></template>
            <template v-slot:item.amount="{ item }"><strong>{{ item.amount | currency }}</strong></template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small aria-label="Edit expense" @click="edit(item)"><v-icon small>mdi-pencil-outline</v-icon></v-btn>
              <v-btn icon small color="error" aria-label="Delete expense" @click="remove(item)"><v-icon small>mdi-delete-outline</v-icon></v-btn>
            </template>
            <template v-slot:no-data><div class="department-expenses__empty"><v-icon color="grey lighten-1">mdi-receipt-text-outline</v-icon><span>No monthly expenses allocated yet.</span></div></template>
          </v-data-table>
        </section>
      </v-card-text>
      <v-card-actions class="profile-dialog-actions entity-form-dialog__actions">
        <v-spacer />
        <v-btn text :disabled="saving" @click="close">Close</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import classService from "@/modules/Classes/service.js";
import teacherService from "@/modules/Teacher/service.js";

const emptyForm = () => ({ category: "TEACHER", TeacherId: null, name: "", amount: null, note: "", staffRole: "", totalAmount: null, ClassIds: [], allocationMode: "EQUAL", allocations: {} });

export default {
  name: "ManageDepartmentExpenses",
  props: { value: Boolean, department: { type: Object, default: null } },
  data() {
    return {
      expenses: [], teachers: [], departments: [], form: emptyForm(), editingId: null, editingGroupKey: null, submitted: false,
      loading: false, loadingTeachers: false, saving: false,
      categories: [{ text: "Teacher · this department", value: "TEACHER" }, { text: "Shared staff / principal", value: "STAFF" }, { text: "Other expense", value: "OTHER" }],
      allocationModes: [{ text: "Split equally", value: "EQUAL" }, { text: "Custom percentages", value: "PERCENTAGE" }],
      headers: [
        { text: "Category", value: "category" }, { text: "Expense", value: "name" },
        { text: "Note", value: "note" }, { text: "Monthly amount", value: "amount", align: "right" },
        { text: "", value: "actions", sortable: false, align: "right", width: "100px" },
      ],
    };
  },
  computed: {
    visible: { get() { return this.value; }, set(value) { if (!value) this.close(); } },
    total() { return this.expenses.reduce((sum, item) => sum + Number(item.amount || 0), 0); },
    selectedDepartments() { return this.form.ClassIds.map(id => this.departments.find(item => item.id === id)).filter(Boolean); },
    equalShare() { return this.form.ClassIds.length ? Number(this.form.totalAmount || 0) / this.form.ClassIds.length : 0; },
    percentTotal() { return this.form.ClassIds.reduce((sum, id) => sum + Number(this.form.allocations[id] || 0), 0); },
    validPercentTotal() { return Math.abs(this.percentTotal - 100) < 0.001; },
    valid() {
      if (this.form.category === "STAFF") return Boolean(this.form.TeacherId) && Boolean(this.form.staffRole) && Number(this.form.totalAmount) > 0 && this.form.ClassIds.length >= 2 && (this.form.allocationMode === "EQUAL" || this.validPercentTotal);
      return Number(this.form.amount) > 0 && (this.form.category === "TEACHER" ? Boolean(this.form.TeacherId) : Boolean(this.form.name));
    },
  },
  watch: {
    value(open) { if (open) this.load(); },
  },
  methods: {
    async load() {
      if (!this.department) return;
      this.loading = true;
      this.loadingTeachers = true;
      try {
        const [expenses, teachers, departments] = await Promise.all([
          classService.getExpenses(this.department.id), teacherService.getAll({ limit: 10000 }), classService.getAll(),
        ]);
        this.expenses = expenses.data || [];
        this.teachers = (teachers.data || []).filter(teacher => teacher.isActive !== false);
        this.departments = departments.data || [];
        if (!this.form.ClassIds.length) this.form.ClassIds = [this.department.id];
      } catch (error) { this.showError(error, "Unable to load department expenses."); }
      finally { this.loading = false; this.loadingTeachers = false; }
    },
    categoryChanged() { const category = this.form.category; this.form = { ...emptyForm(), category, ClassIds: category === "STAFF" && this.department ? [this.department.id] : [] }; this.editingId = null; this.editingGroupKey = null; },
    resetForm() { this.form = { ...emptyForm(), ClassIds: this.department ? [this.department.id] : [] }; this.editingId = null; this.editingGroupKey = null; this.submitted = false; },
    async edit(item) {
      this.submitted = false;
      if (item.allocationGroupKey) {
        this.loading = true;
        try {
          const response = await classService.getSharedExpense(item.allocationGroupKey);
          const rows = response.data || [];
          const first = rows[0];
          this.editingGroupKey = item.allocationGroupKey;
          this.editingId = null;
          this.form = { category: "STAFF", TeacherId: first.TeacherId, name: first.name, amount: null, note: first.note || "", staffRole: first.staffRole || "", totalAmount: Number(first.totalAmount), ClassIds: rows.map(row => row.ClassId), allocationMode: first.allocationMode || "EQUAL", allocations: rows.reduce((result, row) => ({ ...result, [row.ClassId]: Number(row.allocationPercent) }), {}) };
        } catch (error) { this.showError(error, "Unable to load the shared staff expense."); }
        finally { this.loading = false; }
        return;
      }
      this.editingId = item.id;
      this.editingGroupKey = null;
      this.form = { ...emptyForm(), category: item.category, TeacherId: item.TeacherId, name: item.name, amount: Number(item.amount), note: item.note || "" };
    },
    async save() {
      this.submitted = true;
      if (!this.valid) return;
      this.saving = true;
      try {
        if (this.form.category === "STAFF") {
          const payload = { ...this.form, totalAmount: Number(this.form.totalAmount), allocations: this.form.ClassIds.map(ClassId => ({ ClassId, percent: Number(this.form.allocations[ClassId] || 0) })) };
          if (this.editingGroupKey) await classService.updateSharedExpense(this.editingGroupKey, payload);
          else await classService.createSharedExpense(payload);
        } else {
          const payload = { ...this.form, amount: Number(this.form.amount) };
          if (this.editingId) await classService.updateExpense(this.department.id, this.editingId, payload);
          else await classService.createExpense(this.department.id, payload);
        }
        await this.load();
        this.resetForm();
        this.$emit("changed");
      } catch (error) { this.showError(error, "Unable to save the expense."); }
      finally { this.saving = false; }
    },
    async remove(item) {
      const shared = Boolean(item.allocationGroupKey);
      const answer = await this.$swal({ title: "Delete expense?", text: shared ? `${item.name}'s shared salary will be removed from every allocated department.` : `${item.name} will be removed from this department.`, icon: "warning", showCancelButton: true, confirmButtonText: "Delete" });
      if (!answer.value) return;
      try { if (shared) await classService.deleteSharedExpense(item.allocationGroupKey); else await classService.deleteExpense(this.department.id, item.id); await this.load(); this.resetForm(); this.$emit("changed"); }
      catch (error) { this.showError(error, "Unable to delete the expense."); }
    },
    close() { this.resetForm(); this.$emit("input", false); },
    allocatedAmount(ClassId) { return Number(this.form.totalAmount || 0) * Number(this.form.allocations[ClassId] || 0) / 100; },
    categoryLabel(item) { if (item.category === "STAFF") return "Shared staff"; return item.category === "TEACHER" ? "Teacher" : "Other"; },
    showError(error, fallback) { this.$swal("Department expenses", error && error.data && error.data.message ? error.data.message : fallback, "error"); },
  },
};
</script>

<style scoped>
.department-expenses__form-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 18px; }
.department-expenses__form-actions .v-btn { min-height: 42px; border-radius: 9px; text-transform: none; letter-spacing: 0; }
.department-expenses__total strong { margin-left: auto; color: #253047; }
.department-expenses__table { margin: 14px -4px -8px; }
.department-expenses__allocation-grid { overflow: hidden; border: 1px solid #e0e8e1; border-radius: 10px; }
.department-expenses__allocation-row, .department-expenses__allocation-total { display: grid; grid-template-columns: minmax(0, 1fr) 130px 130px; gap: 14px; padding: 10px 14px; align-items: center; border-bottom: 1px solid #edf1ed; }
.department-expenses__allocation-row .v-input { margin: 0; }
.department-expenses__allocation-row strong, .department-expenses__allocation-total strong { text-align: right; }
.department-expenses__allocation-total { border-bottom: 0; background: #f6faf6; }
.department-expenses__empty { display: flex; min-height: 110px; flex-direction: column; gap: 8px; align-items: center; justify-content: center; color: #8a958d; }
@media (max-width: 600px) { .department-expenses__form-actions { flex-direction: column-reverse; } .department-expenses__form-actions .v-btn { width: 100%; } .department-expenses__allocation-row, .department-expenses__allocation-total { grid-template-columns: 1fr 100px; } .department-expenses__allocation-row strong { grid-column: 1 / -1; } }
</style>
